import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, Subject } from 'rxjs';
import { BarcodesApi } from '../api/barcodes/barcodes-api.service';
import { BarcodeBase } from '../api/barcodes/entities/barcode-base.entity';
import { BarcodeSearchResult } from '../api/barcodes/entities/barcode-search-result.entity';
import { Barcode } from '../api/barcodes/entities/barcode.entity';
import { ProductAction } from '../api/products/entities/product-action.entity';
import { Product } from '../api/products/entities/product.entity';
import { ProductsApi } from '../api/products/products-api.service';
import { PurchaseBase } from '../api/purchases/entities/purchase-base.entity';
import { PurchasesApi } from '../api/purchases/purchases-api.service';
import { QuantityUnit } from '../api/quantity-units/entities/quantity-unit.entity';
import { StockItemsApi } from '../api/stock-items/stock-items-api.service';
import { ProductActionType } from '../enums/product-actions/product-action-type.enum';
import { DashboardForms } from '../models/dashboard-forms.model';
import { DashboardState } from '../models/dashboard-state.model';
import { ProductWithBarcode } from '../models/product-with-barcode.model';
import { StaticDataService } from './static-data.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  forms = new DashboardForms();
  product?: ProductWithBarcode;
  state = new DashboardState();

  submission: Subject<Product | undefined> = new Subject<Product | undefined>();

  constructor(
    private barcodesApi: BarcodesApi,
    private matSnackBar: MatSnackBar,
    private productsApi: ProductsApi,
    private purchasesApi: PurchasesApi,
    private staticDataService: StaticDataService,
    private stockItemsApi: StockItemsApi
  ) {
    forkJoin({
      locations: this.staticDataService.getLocations(),
      stores: this.staticDataService.getStores(),
    }).subscribe(({ locations, stores }) => {
      this.forms.quickAction.consume.options.locations = locations;
      this.forms.quickAction.open.options.locations = locations;
      this.forms.quickAction.transfer.options.locations = locations;
      this.forms.shopping.item.options.locations = locations;
      this.forms.shopping.session.options.stores = stores;
    });
  }

  clearSelectedProduct(): void {
    this.clearForms(true);
    this.product = undefined;
    this.state.isLoading = false;
  }

  get quantityUnit(): QuantityUnit {
    if (this.product) {
      return this.state.productActionType === ProductActionType.Purchase
        ? this.product.quantityUnitPurchase
        : this.product.quantityUnitStock;
    } else throw new Error('No product selected');
  }

  private clearForms(clearProduct: boolean = false): void {
    if (clearProduct) {
      this.forms.product.clear();
    }

    this.forms.shopping.item.product = undefined;

    for (const quickActionForm of Object.values(this.forms.quickAction)) {
      quickActionForm.clear();
    }
  }

  findProductByBarcode(barcode: string): void {
    this.disableForms();
    this.state.isLoadingByBarcode = true;

    this.barcodesApi.findBarcodeProduct(barcode).subscribe((result: BarcodeSearchResult | undefined) => {
      if (!result) {
        this.clearForms();
        this.product = undefined;
        this.submission.next(undefined);
        this.state.isSubmitting = false;

        setTimeout(() => {
          this.state.isLoading = false;
          this.enableForms();
        }, 500);

        return;
      }

      const product: ProductWithBarcode = { ...result.product, barcode: result.barcode };

      this.forms.product.options.products = [];
      this.forms.product.controls.product.setValue(product.name);
      this.forms.product.barcodeExists = true;

      this.loadSelectedProduct(product);
    });
  }

  findProductByName(productId: number): void {
    if (this.forms.product.barcodeExists) {
      this.forms.product.controls.barcode.setValue(null);
    }

    this.disableForms();
    this.state.isLoadingByProductId = true;

    this.productsApi.findOne(productId).subscribe((product: Product) => {
      this.loadSelectedProduct(product);
    });
  }

  loadSelectedProduct(product: ProductWithBarcode): void {
    this.forms.shopping.item.clear();
    this.forms.shopping.item.product = product;
    this.product = product;

    for (const quickActionForm of Object.values(this.forms.quickAction)) {
      quickActionForm.product = product;
    }

    if (this.state.isSubmitting) {
      this.submission.next(product);
      this.state.isSubmitting = false;
    }

    setTimeout(() => {
      this.state.isLoading = false;
      this.enableForms();
    }, 500);
  }

  submitQuickAction(productAction: ProductAction): Observable<Product> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      this.productsApi
        .executeProductAction(this.state.productActionType, productAction)
        .subscribe((product: Product) => {
          subscriber.next(product);
          subscriber.complete();
          this.postProcessProductAction(productAction);
        });
    });
  }

  submitRegisteredPurchase(): Observable<PurchaseBase[]> {
    throw new Error('Method not implemented.');
    // this.disableForms();
    // this.state.isSubmitting = true;
    // return new Observable(subscriber => {
    //   const data: PurchaseBase = {
    //     actualPrice: this.forms.shopping.item!.actualPrice,
    //     amount: this.forms.shopping.item!.amount,
    //     fullPrice: this.forms.shopping.item!.fullPrice,
    //     productId: this.selectedProduct?.product.id,
    //     productName: this.selectedProduct?.product.name,
    //     totalDiscount: this.forms.shopping.item!.totalDiscount,
    //     totalPant: this.forms.shopping.item!.totalPant,
    //     unitPant: this.forms.shopping.item!.purchaseQuPant,
    //     unitPrice: this.forms.shopping.item!.purchaseQuPrice,
    //   };
    //   const productAction = this.forms.shopping.item!.getProductAction(
    //     this.forms.shopping.session.date!,
    //     this.forms.shopping.session.store!
    //   );
    //   this.purchasesApi.create(data).subscribe((recentPurchases: Purchase[]) => {
    //     subscriber.next(recentPurchases);
    //     this.postProcessProductAction(productAction);
    //   });
    // });
  }

  private get barcodeData(): BarcodeBase {
    if (this.product) {
      return {
        amount: this.forms.shopping.item.amount,
        barcode: this.forms.product.barcode!,
        note: '',
        productId: this.product.id,
        quantityUnitId: this.quantityUnit.id,
      };
    } else throw new Error('No product selected');
  }

  private constructProductActionSnackbarMessage(success: boolean, { amount }: ProductAction): string {
    const quantityUnitString = amount === 1 ? this.quantityUnit.name : this.quantityUnit.namePlural;

    const message = `${this.state.productActionType.toLowerCase()} of ${amount} ${quantityUnitString} ${this.product?.name}`;

    return success ? `Succeeded ${message}` : `Failed to execute ${message}`;
  }

  private postProcessProductAction(productAction: ProductAction): void {
    let message = this.constructProductActionSnackbarMessage(true, productAction);

    if (this.forms.product.barcodeExists || !this.forms.product.barcode) {
      this.matSnackBar.open(message, 'Dismiss', { duration: 5000 });
      this.findProductByName(productAction.productId!);
    } else {
      this.barcodesApi.create(this.barcodeData).subscribe((barcode: Barcode) => {
        message += ` and successfully linked ${barcode.barcode} to ${this.product?.name}`;
        this.matSnackBar.open(message, 'Dismiss', { duration: 5000 });
        this.findProductByName(productAction.productId!);
      });
    }
  }

  private disableForms(): void {
    this.forms.product.disable();
    this.forms.quickAction.consume.disable();
    this.forms.quickAction.open.disable();
    this.forms.quickAction.transfer.disable();
    this.forms.shopping.item.disable();
  }

  private enableForms(): void {
    this.forms.product.enable();
    this.forms.quickAction.consume.enable();
    this.forms.quickAction.open.enable();
    this.forms.quickAction.transfer.enable();
    this.forms.shopping.item.enable();
  }
}
